import { getClassDisplayName, getClassOptionLabel } from "../../utils/classPresentation";
import ClassPresentation from "../../Components/ClassPresentation";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MenuLateral from "../../Components/MenuLateral";
import { LoginContext } from "../../context/LoginContext";
import { USER_ROLES } from "../../auth/session";
import api from "../../services/api";
import { APP_ROUTES } from "../../auth/access";
import AuthenticatedMedia from "../../Components/AuthenticatedMedia";
import Swal from "sweetalert2";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiHelpCircle,
  FiImage,
  FiList,
  FiLoader,
  FiSearch,
  FiXCircle,
  FiSlash,
  FiCheck,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";
import "./index.css";

const MATCH_TYPE_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "student", label: "Aluno reconhecido" },
  { value: "unknown", label: "Desconhecidos" },
  { value: "other", label: "Outros" },
  { value: "unmatched", label: "Sem reconhecimento" },
];

const PROCESSING_STATUS_BADGES = {
  pending: { label: "Reconhecimento Pendente", icon: FiClock },
  queued: { label: "Aguardando Reconhecimento", icon: FiList },
  processing: { label: "Processando Reconhecimento", icon: FiLoader },
  processed: { label: "Reconhecimento Processado", icon: FiCheckCircle },
  failed: { label: "Falha no Reconhecimento", icon: FiAlertCircle },
  skipped: { label: "Ignorado", icon: FiSlash },
};

const RECOGNITION_OUTCOME_BADGES = {
  MATCHED: { label: "Match", icon: FiUserCheck },
  NO_MATCH: { label: "Sem Match", icon: FiUsers },
  GENERAL_CANDIDATE: { label: "Possível foto geral", icon: FiImage },
  FAILED: { label: "Falha", icon: FiAlertTriangle },
};

const PUBLICATION_STATUS_LABELS = {
  UNPUBLISHED: "Indisponível",
  PUBLISHED: "Publicado",
};

const BATCH_STATUS_LABELS = {
  pending: "Aguardando envio",
  uploading: "Enviando arquivos",
  completed: "Concluído",
  partial_failed: "Concluído com falhas",
  failed: "Falhou",
};

const PUBLICATION_DISTRIBUTION_SEGMENTS = [
  { key: "pending", label: "Pendente", color: "#f0b43c" },
  { key: "matched", label: "Match", color: "#2f6fec" },
  { key: "noMatch", label: "Sem Match", color: "#8ea0b8" },
  { key: "generalCandidate", label: "Fotos gerais", color: "#64748b" },
  { key: "failed", label: "Falha", color: "#a33b3b" },
  { key: "skipped", label: "Ignorado", color: "#b8c3d3" },
  { key: "readyToPublish", label: "Disponível", color: "#27835c" },
  { key: "published", label: "Publicado", color: "#176b4f" },
  { key: "unavailable", label: "Indisponível", color: "#64748b" },
  { key: "unclassified", label: "Sem classificação", color: "#cbd5e1" },
];

const numberFormatter = new Intl.NumberFormat("pt-BR");

const formatCount = (value) => numberFormatter.format(Number(value || 0));

function formatPublicationStatus(status) {
  return PUBLICATION_STATUS_LABELS[status] || "Estado não informado";
}

function formatPublicationDate(value) {
  if (!value) return "-";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("pt-BR");
}

function formatBatchStatus(status) {
  return BATCH_STATUS_LABELS[status] || "Estado não informado";
}

function PublicationDistributionBar({ distribution }) {
  const total = Number(distribution?.total);
  if (!Number.isFinite(total)) return null;

  const segments = PUBLICATION_DISTRIBUTION_SEGMENTS
    .map((segment, index) => ({
      ...segment,
      index,
      value: Number(distribution?.[segment.key] || 0),
    }))
    .filter((segment) => segment.value > 0);

  return (
    <div className="evento-publication-distribution">
      <div className="evento-publication-distribution-header">
        <span>Distribuição das publicações</span>
        <strong>{formatCount(total)} fotos</strong>
      </div>

      <div
        className="evento-publication-distribution-track"
        role="img"
        aria-label={total > 0
          ? `Distribuição de ${formatCount(total)} fotos por estado operacional`
          : "Nenhuma foto disponível para distribuição"}
      >
        {segments.map((segment) => {
          const share = total > 0 ? (segment.value / total) * 100 : 0;

          return (
            <span
              key={segment.key}
              className="evento-publication-distribution-segment"
              style={{
                "--segment-color": segment.color,
                "--segment-order": segment.index,
                "--segment-share": `${share}%`,
              }}
              title={`${segment.label}: ${formatCount(segment.value)} (${Math.round(share)}%)`}
              aria-hidden="true"
            />
          );
        })}
      </div>

      {segments.length ? (
        <ul className="evento-publication-distribution-legend" aria-label="Legenda da distribuição das publicações">
          {segments.map((segment) => (
            <li key={segment.key}>
              <span
                className="evento-publication-distribution-swatch"
                style={{ backgroundColor: segment.color }}
                aria-hidden="true"
              />
              <span className="evento-publication-distribution-label">{segment.label}</span>
              <strong>{formatCount(segment.value)}</strong>
            </li>
          ))}
        </ul>
      ) : (
        <p className="evento-publication-distribution-empty">
          Nenhuma foto foi registrada neste evento.
        </p>
      )}
    </div>
  );
}

const getNomeDoMatch = (match) => {
  if (match.matchType === "student") {
    return match.studentName || match.label || "Aluno reconhecido";
  }

  if (match.matchType === "unknown") {
    return "Desconhecido";
  }

  if (match.matchType === "other") {
    const label = match.label?.trim();

    if (label && !["Outros", "Outro rosto"].includes(label)) {
      return label;
    }

    return "Não classificado";
  }

  return match.label || "Pessoa";
};

const getAvatarIniciais = (name) => {
  const partes = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (partes.length === 0) return "?";

  return partes.map((parte) => parte.charAt(0).toUpperCase()).join("");
};

const getNomeDoAluno = (student) =>
  student?.name || student?.user?.name || student?.email || student?.id || "Aluno";

function EventoDetalhe() {
  const { id: eventId } = useParams();
  const { LoggedUser } = useContext(LoginContext);
  const isAdmin = LoggedUser?.role === USER_ROLES.ADMIN;
  const canViewPublicationSummary =
    LoggedUser?.role === USER_ROLES.ADMIN ||
    LoggedUser?.role === USER_ROLES.PHOTOGRAPHER;

  const [evento, setEvento] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [recognitionSummary, setRecognitionSummary] = useState(null);
  const [loadingRecognitionSummary, setLoadingRecognitionSummary] = useState(false);
  const [publicationSummary, setPublicationSummary] = useState(null);
  const [loadingPublicationSummary, setLoadingPublicationSummary] = useState(false);
  const [publicationSummaryError, setPublicationSummaryError] = useState("");
  const [fotoSelecionada, setFotoSelecionada] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);

  const [filenameSearch, setFilenameSearch] = useState("");
  const [filenameDraft, setFilenameDraft] = useState("");
  const [photosError, setPhotosError] = useState("");
  const photosRequestRef = useRef({ version: 0, controller: null });
  const eventScopeRef = useRef({ id: null, controller: null });
  const [matchType, setMatchType] = useState("all");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [correctionClassId, setCorrectionClassId] = useState("");
  const [correctionStudents, setCorrectionStudents] = useState([]);
  const [correctionSelectedStudents, setCorrectionSelectedStudents] = useState([]);
  const [correctionStudentSearch, setCorrectionStudentSearch] = useState("");
  const [isCorrectionStudentDropdownOpen, setIsCorrectionStudentDropdownOpen] = useState(false);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [removingStudentId, setRemovingStudentId] = useState(null);
  const unassignmentPendingRef = useRef(false);

  const classDropdownRef = useRef(null);
  const studentDropdownRef = useRef(null);
  const correctionStudentDropdownRef = useRef(null);

  const correctionBusy = savingCorrection || Boolean(removingStudentId);
  const galleryBusy = reprocessing || Boolean(deletingPhotoId) || correctionBusy;
  const canFilterStudents = matchType === "student";
  const canSelectStudent = canFilterStudents && !!selectedClassId;
  const classes = evento?.classes || [];
  const selectedClass = classes.find((item) => item.id === selectedClassId) || null;
  const filteredClasses = classes.filter((item) => {
    const query = classSearch.trim().toLowerCase();

    if (!query) return true;

    return `${getClassOptionLabel(item)} ${item.graduationYear || ""}`.toLowerCase().includes(query);
  });
  const pessoasDaFoto = (() => {
    if (!fotoSelecionada?.matches?.length) return [];

    const agrupados = new Map();

    fotoSelecionada.matches.forEach((match) => {
      const nomeBase = getNomeDoMatch(match);

      const chave = match.studentId || `${match.matchType}:${nomeBase}`;
      const atual = agrupados.get(chave);

      if (atual) {
        atual.quantidade += 1;
        return;
      }

      agrupados.set(chave, {
        chave,
        nome: nomeBase,
        tipo: match.matchType,
        studentId: match.studentId,
        quantidade: 1,
        avatarUrl: "",
        iniciais: getAvatarIniciais(nomeBase),
      });
    });

    return Array.from(agrupados.values()).sort((a, b) => {
      if (a.tipo === b.tipo) {
        return a.nome.localeCompare(b.nome, "pt-BR");
      }

      if (a.tipo === "student") return -1;
      if (b.tipo === "student") return 1;

      return a.nome.localeCompare(b.nome, "pt-BR");
    });
  })();
  const alunosReconhecidos = pessoasDaFoto.filter((pessoa) => pessoa.tipo === "student");
  const deteccoesComplementares = pessoasDaFoto.filter((pessoa) => pessoa.tipo !== "student");
  const linkedStudentIds = new Set(
    (fotoSelecionada?.matches || [])
      .filter((match) => match.matchType === "student" && match.studentId)
      .map((match) => match.studentId),
  );
  const availableCorrectionStudents = correctionStudents
    .filter((student) => !linkedStudentIds.has(student.id))
    .sort((a, b) => getNomeDoAluno(a).localeCompare(getNomeDoAluno(b), "pt-BR", {
      sensitivity: "base",
    }));

  const abrirModalFoto = (foto) => {
    if (modoEdicao) return;
    setFotoSelecionada(foto);
  };

  const fecharModalFoto = () => {
    setFotoSelecionada(null);
  };

  const mensagemDoErro = (error) => {
    if (!error) return "Erro desconhecido";

    if (typeof error === "string") return error;

    const msg = error.response?.data?.message;

    if (Array.isArray(msg)) return msg.join(", ");
    if (typeof msg === "string") return msg;

    if (error.message) return error.message;

    return "Erro inesperado ao processar a requisição.";
  };

  const formatarTurmas = (eventClasses = []) => {
    if (eventClasses.length === 0) return null;
    return eventClasses.map((item) => getClassDisplayName(item)).join(", ");
  };

  const formatarInstituicoes = (eventClasses = []) => {
    const instituicoes = Array.from(
      new Set(
        eventClasses
          .map((item) => item.institution?.name)
          .filter(Boolean),
      ),
    );

    if (instituicoes.length === 0) return null;
    return instituicoes.join(", ");
  };

  const obterParametrosFotos = (pagina, overrides = {}) => {
    const nextMatchType = overrides.matchType ?? matchType;
    const nextClassId = overrides.classId ?? selectedClassId;
    const nextStudentId = overrides.studentId ?? selectedStudentId;
    const nextSearch = overrides.search ?? filenameSearch;

    const params = {
      page: pagina,
      limit: 40,
      matchType: nextMatchType,
    };

    if (nextSearch) params.search = nextSearch;

    if (nextMatchType === "student" && nextClassId) {
      params.classId = nextClassId;
    }

    if (nextMatchType === "student" && nextClassId && nextStudentId) {
      params.studentId = nextStudentId;
    }

    return params;
  };

  const carregarEvento = async () => {
    const scope = eventScopeRef.current;
    if (scope.id !== eventId) return;
    const current = () => eventScopeRef.current === scope && !scope.controller.signal.aborted;

    try {
      const resp = await api.get(`/events/${eventId}`, { signal: scope.controller.signal });
      if (!current()) return;
      setEvento(resp.data);
    } catch (err) {
      if (!current()) return;
      Swal.fire("Erro", mensagemDoErro(err), "error");
    }
  };

  const carregarFotos = async (pagina = 1, overrides = {}) => {
    const scope = eventScopeRef.current;
    if (scope.id !== eventId) return;
    photosRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const version = photosRequestRef.current.version + 1;
    photosRequestRef.current = { version, controller };
    const current = () => eventScopeRef.current === scope &&
      photosRequestRef.current.version === version && !controller.signal.aborted;
    const params = obterParametrosFotos(pagina, overrides);
    setLoading(true);
    setPhotosError("");
    setPhotos([]);
    setTotalPhotos(0);
    setTotalPages(0);
    setPage(pagina);
    try {
      let response;
      while (true) {
        response = await api.get(`/upload/event-photos/${eventId}`, {
          params: { ...params }, signal: controller.signal,
        });
        if (!current()) return;
        const lastPage = Math.max(1, response.data.pagination?.totalPages || 0);
        if (params.page <= lastPage) break;
        params.page = lastPage;
      }
      setPhotos(response.data.photos || []);
      setTotalPages(response.data.pagination?.totalPages || 0);
      setTotalPhotos(response.data.pagination?.totalPhotos || 0);
      setPage(params.page);
      carregarResumoReconhecimento();
      carregarResumoPublicacao();
    } catch (err) {
      if (!current()) return;
      setPhotosError(mensagemDoErro(err));
    } finally {
      if (current()) setLoading(false);
    }
  };

  const carregarResumoReconhecimento = async () => {
    const scope = eventScopeRef.current;
    if (scope.id !== eventId) return;
    const current = () => eventScopeRef.current === scope && !scope.controller.signal.aborted;

    setLoadingRecognitionSummary(true);
    try {
      const resp = await api.get(`/upload/event-photos/${eventId}/recognition-summary`, { signal: scope.controller.signal });
      if (!current()) return;
      setRecognitionSummary(resp.data);
    } catch (err) {
      if (!current()) return;
      setRecognitionSummary(null);
      Swal.fire("Erro", mensagemDoErro(err), "error");
    } finally {
      if (current()) setLoadingRecognitionSummary(false);
    }
  };

  const carregarResumoPublicacao = async () => {
    const scope = eventScopeRef.current;
    if (scope.id !== eventId) return;
    const current = () => eventScopeRef.current === scope && !scope.controller.signal.aborted;

    if (!canViewPublicationSummary) return;

    setLoadingPublicationSummary(true);
    setPublicationSummaryError("");

    try {
      const resp = await api.get(`/events/${eventId}/publication`, { signal: scope.controller.signal });
      if (!current()) return;
      setPublicationSummary(resp.data?.publication || null);
    } catch (err) {
      if (!current()) return;
      setPublicationSummary(null);
      setPublicationSummaryError(mensagemDoErro(err));
    } finally {
      if (current()) setLoadingPublicationSummary(false);
    }
  };

  const carregarAlunosCorrecao = (classId) => {
    setCorrectionClassId(classId);
    setCorrectionSelectedStudents([]);
    setCorrectionStudentSearch("");
    setCorrectionStudents([]);
    setIsCorrectionStudentDropdownOpen(false);
  };

  useEffect(() => {
    if (!eventId) return undefined;
    const scope = { id: eventId, controller: new AbortController() };
    eventScopeRef.current = scope;
    setEvento(null);
    setRecognitionSummary(null);
    setPublicationSummary(null);
    setFotoSelecionada(null);
    setFilenameSearch("");
    setFilenameDraft("");
    setMatchType("all");
    setSelectedClassId("");
    setSelectedStudentId("");
    setSelectedStudentName("");
    setClassSearch("");
    setStudentSearch("");
    setStudentOptions([]);
    setIsClassDropdownOpen(false);
    setIsStudentDropdownOpen(false);
    carregarEvento();
    carregarResumoReconhecimento();
    carregarFotos(1, { matchType: "all", classId: "", studentId: "", search: "" });
    return () => {
      scope.controller.abort();
      photosRequestRef.current.controller?.abort();
      photosRequestRef.current.version += 1;
      if (eventScopeRef.current === scope) eventScopeRef.current = { id: null, controller: null };
    };
    // O evento da rota e o gatilho completo da carga inicial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, canViewPublicationSummary]);

  useEffect(() => {
    if (!eventId || !canSelectStudent) {
      setStudentOptions([]);
      setStudentsLoading(false);
      return undefined;
    }

    let ativo = true;

    const timeoutId = window.setTimeout(async () => {
      setStudentsLoading(true);
      try {
        const resp = await api.get("/students", {
          params: {
            classId: selectedClassId,
            search: studentSearch || undefined,
            page: 1,
            limit: 20,
          },
        });

        if (!ativo) return;

        setStudentOptions(resp.data.students || []);
      } catch (err) {
        if (!ativo) return;
        setStudentOptions([]);
        Swal.fire("Erro", mensagemDoErro(err), "error");
      } finally {
        if (ativo) {
          setStudentsLoading(false);
        }
      }
    }, 300);

    return () => {
      ativo = false;
      window.clearTimeout(timeoutId);
    };
  }, [eventId, canSelectStudent, selectedClassId, studentSearch]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        classDropdownRef.current &&
        !classDropdownRef.current.contains(event.target)
      ) {
        setIsClassDropdownOpen(false);
        setClassSearch("");
      }

      if (
        studentDropdownRef.current &&
        !studentDropdownRef.current.contains(event.target)
      ) {
        setIsStudentDropdownOpen(false);
        setStudentSearch("");
      }

    };

    // Close after the click so collapsing the in-flow list cannot move its target.
    const handleCorrectionOutsideClick = (event) => {
      if (
        correctionStudentDropdownRef.current &&
        !correctionStudentDropdownRef.current.contains(event.target)
      ) {
        setIsCorrectionStudentDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("click", handleCorrectionOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("click", handleCorrectionOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!fotoSelecionada?.photoId || !correctionClassId) {
      setCorrectionStudents([]);
      setCorrectionLoading(false);
      return undefined;
    }

    let ativo = true;

    const timeoutId = window.setTimeout(async () => {
      setCorrectionLoading(true);
      try {
        const resp = await api.get("/students", {
          params: {
            classId: correctionClassId,
            search: correctionStudentSearch.trim() || undefined,
            page: 1,
            limit: 100,
          },
        });

        if (!ativo) return;

        setCorrectionStudents(resp.data.students || []);
      } catch (err) {
        if (!ativo) return;
        setCorrectionStudents([]);
        Swal.fire("Erro", mensagemDoErro(err), "error");
      } finally {
        if (ativo) {
          setCorrectionLoading(false);
        }
      }
    }, 280);

    return () => {
      ativo = false;
      window.clearTimeout(timeoutId);
    };
  }, [fotoSelecionada?.photoId, correctionClassId, correctionStudentSearch]);

  useEffect(() => {
    setCorrectionClassId("");
    setCorrectionStudents([]);
    setCorrectionSelectedStudents([]);
    setCorrectionStudentSearch("");
    setIsCorrectionStudentDropdownOpen(false);
  }, [fotoSelecionada?.photoId]);

  const aplicarMatchType = async (nextMatchType) => {
    setMatchType(nextMatchType);
    setSelectedClassId("");
    setSelectedStudentId("");
    setSelectedStudentName("");
    setClassSearch("");
    setStudentSearch("");
    setStudentOptions([]);
    setIsClassDropdownOpen(false);
    setIsStudentDropdownOpen(false);
    setFotoSelecionada(null);

    await carregarFotos(1, {
      matchType: nextMatchType,
      classId: "",
      studentId: "",
    });
  };

  const aplicarTurma = async (nextClassId) => {
    setSelectedClassId(nextClassId);
    setSelectedStudentId("");
    setSelectedStudentName("");
    setClassSearch("");
    setStudentSearch("");
    setStudentOptions([]);
    setIsClassDropdownOpen(false);
    setIsStudentDropdownOpen(false);
    setFotoSelecionada(null);

    await carregarFotos(1, {
      classId: nextClassId,
      studentId: "",
    });
  };

  const aplicarAluno = async (nextStudentId) => {
    setSelectedStudentId(nextStudentId);
    setSelectedStudentName(
      nextStudentId
        ? studentOptions.find((item) => item.id === nextStudentId)?.name || ""
        : "",
    );
    setStudentSearch("");
    setIsStudentDropdownOpen(false);
    setFotoSelecionada(null);

    await carregarFotos(1, {
      studentId: nextStudentId,
    });
  };

  const aplicarBuscaArquivo = (value) => {
    if (galleryBusy) return;
    setFilenameSearch(value);
    setFilenameDraft(value);
    setFotoSelecionada(null);
    carregarFotos(1, { search: value });
  };

  const limparFiltros = async () => {
    setFilenameSearch("");
    setFilenameDraft("");
    setMatchType("all");
    setSelectedClassId("");
    setSelectedStudentId("");
    setSelectedStudentName("");
    setClassSearch("");
    setStudentSearch("");
    setStudentOptions([]);
    setIsClassDropdownOpen(false);
    setIsStudentDropdownOpen(false);
    setFotoSelecionada(null);

    await carregarFotos(1, {
      search: "",
      matchType: "all",
      classId: "",
      studentId: "",
    });
  };

  const excluirFoto = async (photo) => {
    const confirmacao = await Swal.fire({
      title: "Excluir foto?",
      text: `A foto ${photo.originalName || photo.photoId} será removida do armazenamento e do banco.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c53030",
    });

    if (!confirmacao.isConfirmed) return;

    setDeletingPhotoId(photo.photoId);

    try {
      await api.delete(`/upload/event-photos/${eventId}/${photo.photoId}`);

      if (fotoSelecionada?.photoId === photo.photoId) {
        setFotoSelecionada(null);
      }

      const proximaPagina = photos.length === 1 && page > 1 ? page - 1 : page;
      await carregarFotos(proximaPagina);

      Swal.fire("Foto excluída", "A foto foi removida com sucesso.", "success");
    } catch (error) {
      Swal.fire("Erro", mensagemDoErro(error), "error");
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const reprocessarEvento = async () => {
    setReprocessing(true);
    try {
      const resp = await api.post(`/events/${eventId}/recognition/reprocess`);
      await carregarResumoReconhecimento();
      await carregarFotos(page);
      Swal.fire(
        "Reconhecimento enviado",
        `${resp.data?.queuedPhotos || 0} foto(s) foram colocadas na fila.`,
        "success",
      );
    } catch (error) {
      Swal.fire("Erro", mensagemDoErro(error), "error");
    } finally {
      setReprocessing(false);
    }
  };

  const alternarAlunoCorrecao = (student) => {
    if (correctionBusy) return;
    setCorrectionSelectedStudents((current) => {
      const alreadySelected = current.some((item) => item.id === student.id);

      if (alreadySelected) {
        return current.filter((item) => item.id !== student.id);
      }

      return [...current, student];
    });
  };

  const vincularAlunoAFoto = async () => {
    if (correctionBusy || unassignmentPendingRef.current) return;
    if (!fotoSelecionada?.photoId || correctionSelectedStudents.length === 0) {
      Swal.fire("Selecione um aluno para vincular a foto.");
      return;
    }

    const sourceMatch = (fotoSelecionada.matches || []).find(
      (match) => match.matchType === "unknown" || match.matchType === "other",
    );

    setSavingCorrection(true);
    try {
      for (const [index, student] of correctionSelectedStudents.entries()) {
        await api.post(
          `/upload/event-photos/${eventId}/${fotoSelecionada.photoId}/matches/student`,
          {
            studentId: student.id,
            matchId: index === 0 ? sourceMatch?.id : undefined,
          },
        );
      }

      await carregarFotos(page);
      setFotoSelecionada(null);
      const quantidade = correctionSelectedStudents.length;
      Swal.fire(
        "Corrigido",
        `${quantidade} ${quantidade === 1 ? "aluno foi vinculado" : "alunos foram vinculados"} à foto com sucesso.`,
        "success",
      );
    } catch (error) {
      Swal.fire("Erro", mensagemDoErro(error), "error");
    } finally {
      setSavingCorrection(false);
    }
  };

  const desvincularAlunoDaFoto = async (pessoa) => {
    if (!isAdmin || !fotoSelecionada || !pessoa.studentId || galleryBusy || unassignmentPendingRef.current) return;
    const photo = fotoSelecionada;
    const scope = eventScopeRef.current;
    const currentScope = () => eventScopeRef.current === scope && !scope.controller.signal.aborted;
    const alertClass = { container: "event-photo-alert" };
    unassignmentPendingRef.current = true;
    setRemovingStudentId(pessoa.studentId);
    try {
      const content = document.createElement("div");
      const message = document.createElement("p");
      const studentName = document.createElement("strong");
      studentName.textContent = pessoa.nome;
      message.append("Desvincular ", studentName, " da foto abaixo? O aluno deixará de constar como presente nesta foto.");
      const filenameBlock = document.createElement("pre");
      filenameBlock.className = "event-photo-confirmation-filename";
      const filename = document.createElement("code");
      filename.textContent = photo.originalName || photo.photoId;
      filenameBlock.append(filename);
      content.append(message, filenameBlock);
      const confirmation = await Swal.fire({
        title: "Desvincular aluno?",
        html: content,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Desvincular",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#c53030",
        focusCancel: true,
        customClass: alertClass,
      });
      if (!confirmation.isConfirmed || !currentScope()) return;

      const response = await api.delete(
        `/upload/event-photos/${eventId}/${photo.photoId}/matches/student/${pessoa.studentId}`,
      );
      if (!currentScope()) return;
      const updatePhoto = (item) => ({
        ...item,
        matches: (item.matches || []).filter((match) =>
          !(match.matchType === "student" && match.studentId === pessoa.studentId)),
        recognitionOutcome: response.data.recognitionOutcome !== undefined
          ? response.data.recognitionOutcome : item.recognitionOutcome,
      });
      setFotoSelecionada((item) => item?.photoId === photo.photoId ? updatePhoto(item) : item);
      setPhotos((items) => items.map((item) => item.photoId === photo.photoId ? updatePhoto(item) : item));
      await carregarFotos(page);
      if (!currentScope()) return;
      Swal.fire({
        title: "Aluno desvinculado",
        text: `${pessoa.nome} foi desvinculado desta foto.`,
        icon: "success",
        customClass: alertClass,
      });
    } catch (error) {
      if (currentScope()) Swal.fire({ title: "Erro", text: mensagemDoErro(error), icon: "error", customClass: alertClass });
    } finally {
      unassignmentPendingRef.current = false;
      setRemovingStudentId(null);
    }
  };

  const mensagemEstadoVazio = () => {
    if (filenameSearch) return "Nenhuma foto encontrada para o nome de arquivo e os filtros informados.";
    if (matchType === "student" && selectedStudentId) {
      return "Nenhuma foto encontrada para o aluno selecionado.";
    }

    if (matchType === "student" && selectedClassId) {
      return "Nenhuma foto encontrada para a turma selecionada.";
    }

    if (matchType === "unknown") {
      return "Nenhuma foto classificada como Desconhecidos.";
    }

    if (matchType === "other") {
      return "Nenhuma foto classificada como Outros.";
    }

    if (matchType === "unmatched") {
      return "Nenhuma foto sem reconhecimento encontrada.";
    }

    return "Nenhuma foto encontrada para este evento.";
  };

  const turmas = formatarTurmas(classes);
  const instituicoes = formatarInstituicoes(classes);
  const recognitionCounts = recognitionSummary?.counts || {};
  const publicationCounts = publicationSummary?.counts || {};
  const latestBatch = publicationSummary?.batches?.[0] || null;

  return (
    <div className="pagina-cad">
      <MenuLateral />

      <div className="conteudo-cad evento-detalhe-conteudo">
        {evento && (
          <div className="evento-topo">
            <div className="evento-topo-conteudo">
              <span className="evento-topo-kicker">DETALHES DO EVENTO</span>
              <h1>{evento.name}</h1>
              <div className="evento-topo-meta">
                <span className="evento-topo-data">
                  <small>Início</small>
                  <strong>{new Date(evento.eventDate).toLocaleDateString("pt-BR")}</strong>
                </span>
                {evento.endDate ? (
                  <span className="evento-topo-data">
                    <small>Término</small>
                    <strong>{new Date(evento.endDate).toLocaleDateString("pt-BR")}</strong>
                  </span>
                ) : null}
                <span className="evento-topo-local">
                  <small>Local</small>
                  <strong>{evento.location || "Local não informado"}</strong>
                </span>
              </div>
              {(turmas || instituicoes) && (
                <div className="evento-topo-contexto">
                  {turmas && <span style={{ overflowWrap: "anywhere" }}>{turmas}</span>}
                  {instituicoes && <small className="evento-topo-instituicao">{instituicoes}</small>}
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="evento-topo-acoes">
                <button
                  type="button"
                  className={`btn-edicao-evento ${modoEdicao ? "ativo" : ""}`}
                  onClick={() => {
                    setModoEdicao((current) => !current);
                    setFotoSelecionada(null);
                  }}
                >
                  {modoEdicao ? "Concluir edição" : "Editar evento"}
                </button>

                <button
                  type="button"
                  className="btn-reprocessar-evento"
                  onClick={reprocessarEvento}
                  disabled={reprocessing}
                >
                  {reprocessing ? "Enfileirando..." : "Reprocessar reconhecimento"}
                </button>

                <Link
                  className="btn-reprocessar-evento"
                  to={`${APP_ROUTES.RECOGNITION_OPERATIONS}?eventId=${encodeURIComponent(eventId)}`}
                >
                  Abrir aprovação de análise facial
                </Link>

                <Link
                  className="btn-reprocessar-evento"
                  to={`${APP_ROUTES.PUBLICATION_OPERATIONS}?eventId=${encodeURIComponent(eventId)}`}
                >
                  Abrir publicação de fotos
                </Link>

                {modoEdicao && (
                  <span className="evento-topo-ajuda">
                    Clique em "Excluir foto" na imagem desejada.
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="evento-resumos">
        <div className="recognition-summary-card">
          <div className="recognition-summary-main">
            <div>
              <span className="summary-kicker">PROCESSAMENTO</span>
              <strong>Reconhecimento facial</strong>
              <span>
                {loadingRecognitionSummary
                  ? "Atualizando..."
                  : `${recognitionSummary?.completed || 0} de ${recognitionSummary?.total || 0} foto(s) finalizada(s)`}
              </span>
            </div>
            <strong className="recognition-percent">
              {recognitionSummary?.progressPercent || 0}%
            </strong>
          </div>

          <div
            className="recognition-progress-bar"
            role="progressbar"
            aria-label="Progresso do reconhecimento facial"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={recognitionSummary?.progressPercent || 0}
          >
            <div style={{ width: `${recognitionSummary?.progressPercent || 0}%` }} />
          </div>

          <div className="recognition-summary-grid">
            <span><strong>{recognitionCounts.pending || 0}</strong><small>Reconhecimento pendente</small></span>
            <span><strong>{recognitionCounts.queued || 0}</strong><small>Aguardando reconhecimento</small></span>
            <span><strong>{recognitionCounts.processing || 0}</strong><small>Em processamento</small></span>
            <span><strong>{recognitionCounts.processed || 0}</strong><small>Processado</small></span>
            <span><strong>{recognitionCounts.skipped || 0}</strong><small>Ignorado</small></span>
            <span><strong>{recognitionCounts.failed || 0}</strong><small>Com falha</small></span>
          </div>
        </div>

        {canViewPublicationSummary && (
          <section className="publication-summary-card" aria-label="Resumo de publicação">
            <div className="publication-summary-header">
              <div>
                <span className="summary-kicker">PUBLICAÇÃO</span>
                <strong>Resumo de publicação</strong>
                <span>
                  {loadingPublicationSummary
                    ? "Atualizando o estado administrativo..."
                    : "Acompanhe o estado do evento e do último lote."}
                </span>
              </div>
              <div className={`publication-summary-status status-${String(publicationSummary?.publicationStatus || "unknown").toLowerCase()}`}>
                <span>Estado do evento</span>
                <strong>
                  {formatPublicationStatus(publicationSummary?.publicationStatus)}
                </strong>
              </div>
            </div>

            {publicationSummaryError && (
              <div className="publication-summary-error" role="alert">
                {publicationSummaryError}
              </div>
            )}

            {publicationSummary && (
              <>
                <div className="publication-summary-meta">
                  <span>
                    Publicado em: {formatPublicationDate(publicationSummary.publishedAt)}
                  </span>
                </div>

                <PublicationDistributionBar distribution={publicationSummary.distribution} />

                <div className="publication-summary-grid">
                  <span><strong>{publicationCounts.processing || 0}</strong><small>Pendente</small></span>
                  <span><strong>{publicationCounts.matched || 0}</strong><small>Match</small></span>
                  <span><strong>{publicationCounts.noMatch || 0}</strong><small>Sem Match</small></span>
                  <span><strong>{publicationCounts.generalCandidate || 0}</strong><small>Foto geral</small></span>
                  <span><strong>{publicationCounts.failed || 0}</strong><small>Com falha</small></span>
                  <span><strong>{publicationCounts.skipped || 0}</strong><small>Ignorado</small></span>
                  <span><strong>{publicationCounts.readyToPublish || 0}</strong><small>Disponível</small></span>
                  <span><strong>{publicationCounts.published || 0}</strong><small>Publicado</small></span>
                  <span><strong>{publicationCounts.unavailable || 0}</strong><small>Indisponível</small></span>
                </div>

                <div className="publication-batches">
                  <div className="publication-batches-header">
                    <div>
                      <strong>Último lote</strong>
                      <span>Envio mais recente deste evento</span>
                    </div>
                  </div>
                  {latestBatch ? (
                    <div className="publication-batches-list">
                      <article className="publication-batch" key={latestBatch.id}>
                        <div className="publication-batch-heading">
                          <div className="publication-batch-date">
                            <span className="publication-batch-date-label">Criado em</span>
                            <time dateTime={latestBatch.createdAt || undefined}>
                              {formatPublicationDate(latestBatch.createdAt)}
                            </time>
                          </div>
                          <span
                            className={`publication-batch-status status-${latestBatch.status || "unknown"}`}
                          >
                            {formatBatchStatus(latestBatch.status)}
                          </span>
                        </div>
                        <div className="publication-batch-metrics" aria-label="Resumo do lote">
                          <div>
                            <strong>{latestBatch.uploadedFiles ?? 0}</strong>
                            <span>Arquivos enviados</span>
                          </div>
                          <div>
                            <strong>{latestBatch.processedFiles ?? 0}</strong>
                            <span>Arquivos processados</span>
                          </div>
                          <div>
                            <strong>{latestBatch.failedFiles ?? 0}</strong>
                            <span>Falhas</span>
                          </div>
                        </div>
                      </article>
                    </div>
                  ) : (
                    <p className="publication-batches-empty">Nenhum lote registrado para este evento.</p>
                  )}
                </div>
              </>
            )}
          </section>
        )}
        </div>

        <div className="filtros-galeria">
          <div className="filtros-header">
            <div>
              <strong>Exibição das fotos</strong>
              <span>{totalPhotos} foto(s) no resultado atual</span>
            </div>

            <button
              type="button"
              className="btn-filtro-secundario"
              onClick={limparFiltros}
              disabled={galleryBusy}
            >
              Limpar filtros
            </button>
          </div>

          <form className="evento-busca-arquivo" aria-label="Busca por nome do arquivo"
            onSubmit={(event) => {
              event.preventDefault();
              aplicarBuscaArquivo(filenameDraft);
            }}>
            <label htmlFor="evento-filename-search">Nome do arquivo</label>
            <div>
              <input id="evento-filename-search" type="search" maxLength={255}
                placeholder="Buscar pelo nome ou extensão" value={filenameDraft}
                onChange={(event) => setFilenameDraft(event.target.value)} disabled={galleryBusy} />
              <button type="submit" className="btn-filtro-secundario" disabled={galleryBusy}>Buscar</button>
              <button type="button" className="btn-filtro-secundario"
                disabled={galleryBusy || (!filenameSearch && !filenameDraft)}
                onClick={() => aplicarBuscaArquivo("")}>Limpar busca</button>
            </div>
          </form>

          <div className="filtros-grid">
            <label className="filtro-item">
              <span>Classificação</span>
              <select
                value={matchType}
                onChange={(e) => aplicarMatchType(e.target.value)}
                disabled={galleryBusy}
              >
                {MATCH_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="filtro-item">
              <span>Turma</span>
              <div
                ref={classDropdownRef}
                className={`filtro-combobox ${!canFilterStudents || galleryBusy ? "disabled" : ""}`}
              >
                <div className="filtro-combobox-controle">
                  <input
                    type="text"
                    placeholder={canFilterStudents ? "Buscar ou selecionar turma" : "Selecione Aluno reconhecido"}
                    value={isClassDropdownOpen ? classSearch : selectedClass ? getClassOptionLabel(selectedClass) : ""}
                    onFocus={() => {
                      if (!canFilterStudents || galleryBusy) return;
                      setIsClassDropdownOpen(true);
                    }}
                    onChange={(e) => {
                      setClassSearch(e.target.value);
                      setIsClassDropdownOpen(true);
                    }}
                    disabled={!canFilterStudents || galleryBusy}
                  />

                  <button
                    type="button"
                    className="filtro-combobox-toggle"
                    onClick={() => {
                      if (!canFilterStudents || galleryBusy) return;
                      setIsClassDropdownOpen((current) => !current);
                      setClassSearch("");
                    }}
                    disabled={!canFilterStudents || galleryBusy}
                  >
                    ▾
                  </button>
                </div>

                {isClassDropdownOpen && canFilterStudents && (
                  <div className="filtro-combobox-menu">
                    <button
                      type="button"
                      className={`filtro-combobox-opcao ${!selectedClassId ? "selecionada" : ""}`}
                      onClick={() => aplicarTurma("")}
                    >
                      Todas as turmas
                    </button>

                    {filteredClasses.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`filtro-combobox-opcao ${selectedClassId === item.id ? "selecionada" : ""}`}
                        onClick={() => aplicarTurma(item.id)}
                      >
                        <ClassPresentation schoolClass={item} />
                      </button>
                    ))}

                    {filteredClasses.length === 0 && (
                      <div className="filtro-combobox-vazio">
                        Nenhuma turma encontrada.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>

            <label className="filtro-item filtro-item-busca">
              <span>Aluno</span>
              <div
                ref={studentDropdownRef}
                className={`filtro-combobox ${!canSelectStudent || galleryBusy ? "disabled" : ""}`}
              >
                <div className="filtro-combobox-controle">
                  <input
                    type="text"
                    placeholder={canSelectStudent ? "Buscar ou selecionar aluno" : "Selecione uma turma"}
                    value={isStudentDropdownOpen ? studentSearch : selectedStudentName}
                    onFocus={() => {
                      if (!canSelectStudent || galleryBusy) return;
                      setIsStudentDropdownOpen(true);
                    }}
                    onChange={(e) => {
                      setStudentSearch(e.target.value);
                      setIsStudentDropdownOpen(true);
                    }}
                    disabled={!canSelectStudent || galleryBusy}
                  />

                  <button
                    type="button"
                    className="filtro-combobox-toggle"
                    onClick={() => {
                      if (!canSelectStudent || galleryBusy) return;
                      setIsStudentDropdownOpen((current) => !current);
                      setStudentSearch("");
                    }}
                    disabled={!canSelectStudent || galleryBusy}
                  >
                    ▾
                  </button>
                </div>

                {isStudentDropdownOpen && canSelectStudent && (
                  <div className="filtro-combobox-menu">
                    <button
                      type="button"
                      className={`filtro-combobox-opcao ${!selectedStudentId ? "selecionada" : ""}`}
                      onClick={() => aplicarAluno("")}
                    >
                      {studentsLoading ? "Buscando alunos..." : "Todos os alunos"}
                    </button>

                    {studentOptions.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`filtro-combobox-opcao ${selectedStudentId === item.id ? "selecionada" : ""}`}
                        onClick={() => aplicarAluno(item.id)}
                      >
                        {item.name}
                      </button>
                    ))}

                    {!studentsLoading && studentOptions.length === 0 && (
                      <div className="filtro-combobox-vazio">
                        Nenhum aluno encontrado.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {loading && (
          <div className="loading">
            <span>Carregando fotos do evento...</span>
          </div>
        )}

        {photosError ? (
          <div className="evento-fotos-erro" role="alert">
            <p>{photosError}</p>
            <button type="button" className="btn-filtro-secundario" disabled={loading || galleryBusy}
              onClick={() => carregarFotos(page)}>Tentar novamente</button>
          </div>
        ) : null}

        <div className={`fotos-grid ${loading ? "disabled" : ""}`}>
          {photos.map((p) => {
            const deleting = deletingPhotoId === p.photoId;
            const processingStatus = p.processingStatus || "pending";
            const processingBadge = PROCESSING_STATUS_BADGES[processingStatus] || {
              label: p.processingStatus || "Reconhecimento Pendente",
              icon: FiHelpCircle,
            };
            const ProcessingIcon = processingBadge.icon;
            const recognitionBadge = p.recognitionOutcome
              ? RECOGNITION_OUTCOME_BADGES[p.recognitionOutcome] || {
                label: p.recognitionOutcome,
                icon: FiHelpCircle,
              }
              : null;
            const RecognitionIcon = recognitionBadge?.icon;

            return (
              <div
                key={p.photoId}
                className={`foto-card ${modoEdicao ? "editando" : ""}`}
                onClick={() => abrirModalFoto(p)}
                onKeyDown={(event) => {
                  if (!modoEdicao && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    abrirModalFoto(p);
                  }
                }}
                role={!modoEdicao ? "button" : undefined}
                tabIndex={modoEdicao ? -1 : 0}
                aria-label={!modoEdicao ? `Abrir foto ${p.originalName || p.photoId}` : undefined}
              >
                <AuthenticatedMedia
                  path={p.media?.thumbnail?.path}
                  alt="Foto do evento"
                  loading="lazy"
                  decoding="async"
                />

                <span
                  className={`foto-processing-badge ${processingStatus}`}
                  data-tooltip={processingBadge.label}
                  title={processingBadge.label}
                  aria-label={`Processamento: ${processingBadge.label}`}
                  role="img"
                >
                  <ProcessingIcon aria-hidden="true" focusable="false" />
                </span>
                {recognitionBadge ? (
                  <span
                    className={`foto-recognition-badge ${p.recognitionOutcome.toLowerCase()}`}
                    data-tooltip={recognitionBadge.label}
                    title={recognitionBadge.label}
                    aria-label={`Resultado do reconhecimento: ${recognitionBadge.label}`}
                    role="img"
                  >
                    <RecognitionIcon aria-hidden="true" focusable="false" />
                  </span>
                ) : null}

                {modoEdicao && isAdmin && (
                  <div className="foto-acoes">
                    <button
                      type="button"
                      className="btn-excluir-foto"
                      disabled={deleting}
                      onClick={(event) => {
                        event.stopPropagation();
                        excluirFoto(p);
                      }}
                    >
                      {deleting ? "Excluindo..." : "Excluir foto"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {!loading && !photosError && photos.length === 0 && (
            <div className="empty">{mensagemEstadoVazio()}</div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="paginacao paginacao-evento">
            <button disabled={page === 1 || loading} onClick={() => {
              setFotoSelecionada(null);
              carregarFotos(page - 1);
            }}>
              Anterior
            </button>

            <span>
              Página {page} de {totalPages}
            </span>

            <button
              disabled={page === totalPages || loading}
              onClick={() => {
                setFotoSelecionada(null);
                carregarFotos(page + 1);
              }}
            >
              Próxima
            </button>
          </div>
        )}
      </div>

      {fotoSelecionada && !modoEdicao && (
        <div
          className="modal-overlay"
          onClick={fecharModalFoto}
          role="presentation"
        >
          <div
            className="modal-imagem"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Detalhes da foto"
          >
            <div className="modal-imagem-principal">
              <AuthenticatedMedia
                path={fotoSelecionada.media?.preview?.path || fotoSelecionada.media?.thumbnail?.path}
                alt="Foto ampliada"
              />
            </div>

            <aside className="modal-lateral">
              <div className="modal-lateral-header">
                <strong>Pessoas presentes na foto</strong>
                <span>
                  {alunosReconhecidos.length > 0
                    ? `${alunosReconhecidos.length} aluno(s) reconhecido(s)`
                    : "Sem alunos reconhecidos"}
                </span>
                {fotoSelecionada.recognitionOutcome ? (
                  <small className={`modal-recognition-outcome ${fotoSelecionada.recognitionOutcome.toLowerCase()}`}>
                    {RECOGNITION_OUTCOME_BADGES[fotoSelecionada.recognitionOutcome]?.label || fotoSelecionada.recognitionOutcome}
                  </small>
                ) : null}
              </div>

              {alunosReconhecidos.length > 0 ? (
                <div
                  className="modal-pessoas-lista"
                  role="region"
                  aria-label="Alunos presentes na foto"
                  tabIndex={0}
                >
                  {alunosReconhecidos.map((pessoa) => (
                    <div key={pessoa.chave} className="modal-pessoa-card">
                      <div className={`modal-pessoa-avatar ${pessoa.avatarUrl ? "com-foto" : "sem-foto"}`}>
                        {pessoa.avatarUrl ? (
                          <img src={pessoa.avatarUrl} alt={pessoa.nome} loading="lazy" />
                        ) : (
                          <span>{pessoa.iniciais}</span>
                        )}
                      </div>

                      <small title={pessoa.nome}>{pessoa.nome}</small>

                      {isAdmin && pessoa.studentId && (
                        <button
                          type="button"
                          className="modal-pessoa-remover"
                          aria-label={`Desvincular ${pessoa.nome} desta foto`}
                          title={`Desvincular ${pessoa.nome} desta foto`}
                          disabled={galleryBusy}
                          aria-busy={removingStudentId === pessoa.studentId}
                          onClick={() => desvincularAlunoDaFoto(pessoa)}
                        >
                          <FiXCircle aria-hidden="true" focusable="false" />
                        </button>
                      )}

                      {pessoa.quantidade > 1 && (
                        <span className="modal-pessoa-badge">{pessoa.quantidade}x</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="modal-pessoas-vazio">
                  Nenhum aluno reconhecido nesta foto.
                </div>
              )}

              {deteccoesComplementares.length > 0 && (
                <div className="modal-deteccoes-extra">
                  <strong>Outras detecções</strong>

                  <div className="modal-deteccoes-chips">
                    {deteccoesComplementares.map((pessoa) => (
                      <span key={pessoa.chave} className="modal-deteccao-chip">
                        {pessoa.nome}
                        {pessoa.quantidade > 1 ? ` (${pessoa.quantidade})` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="modal-correcao">
                  <strong>Correção manual</strong>

                  <label>
                    <span>Turma</span>
                    <select
                      value={correctionClassId}
                      onChange={(event) => carregarAlunosCorrecao(event.target.value)}
                      disabled={correctionBusy}
                    >
                      <option value="">Selecione a turma</option>
                      {classes.map((item) => (
                        <option key={item.id} value={item.id}>
                          {getClassOptionLabel(item)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Alunos</span>
                    <div
                      className={`modal-correcao-alunos ${
                        !correctionClassId ? "desabilitado" : ""
                      }`}
                      ref={correctionStudentDropdownRef}
                    >
                      <div className="modal-correcao-busca">
                        <FiSearch aria-hidden="true" focusable="false" />
                        <input
                          id="correction-student-search"
                          type="search"
                          role="combobox"
                          aria-label="Buscar aluno por nome"
                          aria-autocomplete="list"
                          aria-controls="correction-student-options"
                          aria-expanded={isCorrectionStudentDropdownOpen}
                          placeholder={
                            correctionClassId
                              ? "Digite o nome ou parte dele"
                              : "Selecione a turma primeiro"
                          }
                          value={correctionStudentSearch}
                          onChange={(event) => {
                            setCorrectionStudentSearch(event.target.value);
                            setIsCorrectionStudentDropdownOpen(true);
                          }}
                          onFocus={() => {
                            if (correctionClassId) {
                              setIsCorrectionStudentDropdownOpen(true);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Escape") {
                              setIsCorrectionStudentDropdownOpen(false);
                            }
                          }}
                          disabled={!correctionClassId || correctionBusy}
                        />
                      </div>

                      {correctionSelectedStudents.length > 0 && (
                        <div className="modal-correcao-selecionados" aria-label="Alunos selecionados">
                          {correctionSelectedStudents.map((student) => (
                            <span key={student.id} className="modal-correcao-chip">
                              {getNomeDoAluno(student)}
                              <button
                                type="button"
                                aria-label={`Remover ${getNomeDoAluno(student)}`}
                                onClick={() => alternarAlunoCorrecao(student)}
                                disabled={correctionBusy}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {isCorrectionStudentDropdownOpen && correctionClassId && (
                        <div
                          id="correction-student-options"
                          className="modal-correcao-opcoes"
                          role="listbox"
                          aria-label="Alunos encontrados"
                          aria-multiselectable="true"
                        >
                          {correctionLoading ? (
                            <div className="modal-correcao-opcao-vazia">Buscando alunos...</div>
                          ) : availableCorrectionStudents.length === 0 ? (
                            <div className="modal-correcao-opcao-vazia">
                              Nenhum aluno disponível para vincular nesta busca.
                            </div>
                          ) : (
                            availableCorrectionStudents.map((student) => {
                              const selected = correctionSelectedStudents.some(
                                (item) => item.id === student.id,
                              );

                              return (
                                <div
                                  key={student.id}
                                  className={`modal-correcao-opcao ${selected ? "selecionada" : ""}`}
                                  role="option"
                                  aria-selected={selected}
                                  tabIndex={0}
                                  onClick={() => alternarAlunoCorrecao(student)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      alternarAlunoCorrecao(student);
                                    }
                                  }}
                                >
                                  <span>{getNomeDoAluno(student)}</span>
                                  {selected && <FiCheck aria-hidden="true" focusable="false" />}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </label>

                  <button
                    type="button"
                    className="btn-vincular-aluno"
                    onClick={vincularAlunoAFoto}
                    disabled={correctionSelectedStudents.length === 0 || correctionBusy}
                  >
                    {correctionSelectedStudents.length > 1
                      ? `Vincular ${correctionSelectedStudents.length} alunos à foto`
                      : "Vincular aluno à foto"}
                  </button>

                  <small className="modal-correcao-ajuda">
                    A vinculação é manual e pode incluir mais de um aluno.
                  </small>

                  <button
                    type="button"
                    className="btn-fechar-modal"
                    onClick={fecharModalFoto}
                  >
                    Fechar
                  </button>
                </div>
              )}
            </aside>

            <button
              type="button"
              className="btn-fechar"
              onClick={fecharModalFoto}
              aria-label="Fechar detalhes da foto"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventoDetalhe;
